import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";
import { formatDate, setExcelDateCell } from "../utils/formatDate";
import XLSX from "xlsx-js-style";

const PobData = () => {
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef(null);
  const handleExpandSearch = () => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const tableWrapperRef = useRef(null);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (showContainers && hasInitialLoad) {
      handleGoClick();
    }
  };

  const getMonthYearOptions = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const options = [];
    const currentDate = new Date();

    const pastMonths = 18;
    const futureMonths = 0;

    for (let i = futureMonths; i >= -pastMonths; i--) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + i,
        1,
      );

      const monthIndex = date.getMonth();
      const year = date.getFullYear();

      options.push({
        label: `${months[monthIndex]} ${year}`,
        value: `${String(monthIndex + 1).padStart(2, "0")}-${year}`,
      });
    }

    return options;
  };
  const monthYearOptions = getMonthYearOptions();

  const currentDate = new Date();
  const currentValue = `${String(currentDate.getMonth() + 1).padStart(
    2,
    "0",
  )}-${currentDate.getFullYear()}`;

  const [pobMonthYear, setPobMonthYear] = useState(currentValue);
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = [];
      if (stateId) params.push(`state_id=${stateId}`);
      if (divisionName && divisionName !== "All")
        params.push(`division=${encodeURIComponent(divisionName)}`);
      if (params.length) url += `?${params.join("&")}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });

      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
      setSelectedEmployee("All");
    } catch (err) {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setError("Failed to load employees");
    }
  }, []);
  useEffect(() => {
    const savedState = localStorage.getItem("pobDataState");
    if (savedState) {
      try {
        const state = JSON.parse(savedState);

        setSelectedState(state.selectedState || "");
        setSelectedDivision(state.selectedDivision || "All");
        setSelectedEmployee(state.selectedEmployee || "All");
        setPobMonthYear(
          state.pobMonthYear || monthYearOptions[new Date().getMonth()].value,
        );
        setTableData(state.tableData || []);
        setShowContainers(
          Boolean(state.tableData && state.tableData.length > 0),
        );
        setHasInitialLoad(
          Boolean(state.tableData && state.tableData.length > 0),
        );

        const restoreStateId = state.selectedState || "";
        axios
          .get(
            `${BASE_URL}/api/divisions${restoreStateId ? `?state_id=${restoreStateId}` : ""
            }`,
            {
              headers: getAuthHeaders(),
            },
          )
          .then((response) => {
            setDivisions([{ id: "All", name: "All" }, ...response.data]);
          })
          .catch((err) => {
            setDivisions([{ id: "All", name: "All" }]);
          });

        const divisionName =
          state.selectedDivision === "All"
            ? "All"
            : state.divisions?.find((d) => d.id === state.selectedDivision)
              ?.name || "All";
        fetchEmployees(restoreStateId, divisionName);

        localStorage.removeItem("pobDataState");
      } catch (err) {
        localStorage.removeItem("pobDataState");
      }
    }
  }, [fetchEmployees, monthYearOptions]);
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const url = `${BASE_URL}/api/divisions${selectedState ? `?state_id=${selectedState}` : ""
          }`;
        const response = await axios.get(url, { headers: getAuthHeaders() });
        setDivisions([{ id: "All", name: "All" }, ...response.data]);
        setSelectedDivision("All");
      } catch (err) {
        setError("Failed to load divisions");
        setDivisions([{ id: "All", name: "All" }]);
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions${stateId ? `?state_id=${stateId}` : ""}`,
        { headers: getAuthHeaders() },
      );
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      setError("Failed to load divisions");
      setDivisions([{ id: "All", name: "All" }]);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);

    const division = divisions.find((d) => String(d.id) === divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [month, year] = pobMonthYear.split("-");
      let url = `${BASE_URL}/api/summary?month=${month}&year=${year}`;

      if (selectedState) url += `&state_id=${selectedState}`;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === selectedDivision,
        );
        if (division?.name)
          url += `&division=${encodeURIComponent(division.name)}`;
      }
      if (selectedEmployee && selectedEmployee !== "All")
        url += `&employee_id=${selectedEmployee}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });

      if (response.data) {
        if (Array.isArray(response.data)) setTableData(response.data);
        else if (response.data.data && Array.isArray(response.data.data))
          setTableData(response.data.data);
        else setTableData([]);
      } else setTableData([]);

      setShowContainers(true);
      setHasInitialLoad(true);
    } catch (err) {
      if (err.response)
        setError(
          `Server error: ${err.response.status}. ${err.response.data?.message || "Please try again."
          }`,
        );
      else if (err.request)
        setError("Network error. Please check your connection and try again.");
      else
        setError("Something went wrong while fetching data. Please try again.");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedEmployee,
    pobMonthYear,
    searchQuery,
    divisions,
  ]);

  useEffect(() => {
    if (showContainers && hasInitialLoad && searchQuery !== "") {
      const timeoutId = setTimeout(() => {
        handleGoClick();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, showContainers, hasInitialLoad, handleGoClick]);
  const printableStyles = `
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; font-size:12px; color:#111; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; word-break: break-word; }
        th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: middle; }
        thead th { background: #2b6cb0; color: #fff; font-weight: 600; text-align: center; }
        .text-center { text-align: center; }
        .text-left { text-align: left; }
        .nowrap { white-space: nowrap; }
        .small { font-size: 11px; }
        a { color: #1d4ed8; text-decoration: underline; }
      </style>
    `;

  const getPrintableTableHtml = () => {
    if (!tableWrapperRef.current) return "<div>No table found</div>";
    const wrapper = tableWrapperRef.current.cloneNode(true);
    wrapper.querySelectorAll("button").forEach((btn) => {
      const span = document.createElement("span");
      span.innerHTML = btn.innerHTML;
      btn.parentNode.replaceChild(span, btn);
    });
    wrapper.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (href) a.insertAdjacentText("afterend", ` (${href})`);
    });

    return wrapper.innerHTML;
  };

  const handlePrint = () => {
    try {
      const tableHtml = getPrintableTableHtml();
      const w = window.open(
        "",
        "_blank",
        "width=1200,height=900,menubar=no,toolbar=no,status=no,scrollbars=yes",
      );
      if (!w) {
        toast.warning("Please allow popups to enable printing.");
        return;
      }

      w.document.open();
      w.document.write(
        `<html><head><title>POB Report - Print</title>${printableStyles}</head><body>`,
      );
      w.document.write(
        `<div style="margin:12px 8px;"><h2 style="margin-bottom:8px;text-align:center">POB Report</h2>`,
      );
      w.document.write(tableHtml);
      w.document.write(
        `<div style="text-align:center;margin-top:12px;font-size:12px">Generated on: ${new Date().toLocaleString()}</div>`,
      );
      w.document.write("</div></body></html>");
      w.document.close();

      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (e) { }
      }, 300);
    } catch (err) {
      toast.error("Unable to print.");
    }
  };

  const handleExportExcel = () => {
    if (tableData.length === 0) {
      toast.info("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};

    // =========================
    // HEADERS
    // =========================
    const headers = [
      "S.No", "EMP ID", "Employee", "State", "HQ", "Division",
      "Designation", "DOJ", "Manager",
      "No Of Doctors", "No Of Retailers", "No Of Stockist", "No Of Route",
      "POB Total", "POB DateWise", "Stockist Wise", "Cumulative Stockist Wise",
    ];

    // =========================
    // STYLES (FIXED)
    // =========================
    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFFFF" }, // white
        sz: 11,
      },
      fill: {
        fgColor: { rgb: "FF2563EB" }, // blue (ARGB)
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "thin", color: { rgb: "FF000000" } },
        bottom: { style: "thin", color: { rgb: "FF000000" } },
        left: { style: "thin", color: { rgb: "FF000000" } },
        right: { style: "thin", color: { rgb: "FF000000" } },
      },
    };

    const rowStyle = {
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "FFBFBFBF" } },
        bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
        left: { style: "thin", color: { rgb: "FFBFBFBF" } },
        right: { style: "thin", color: { rgb: "FFBFBFBF" } },
      },
    };

    // =========================
    // ADD HEADERS
    // =========================
    headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });

      ws[cellAddress] = {
        v: header,
        t: "s",
        s: headerStyle,
      };
    });

    // =========================
    // ADD DATA
    // =========================
    tableData.forEach((row, index) => {
      const empRaw = String(row.user_id ?? "").trim();
      const empIsNum = /^\d+$/.test(empRaw);
      const empID = empIsNum ? Number(empRaw) : empRaw;

      const rowData = [
        index + 1,
        empID,
        row.employee_name || "",
        row.state || "",
        row.hq || "",
        row.division || "",
        row.designation || "",
        row.doj ? new Date(row.doj) : "",
        row.manager || "",
        Number(row.doctor_count || 0),
        Number(row.retailer_count || 0),
        Number(row.stockist_count || 0),
        Number(row.route_count || 0),
        Number(row.monthly_pob || 0),
        Number(row.monthly_pob || 0),
        Number(row.monthly_pob || 0),
        Number(row.monthly_pob || 0),
      ];

      rowData.forEach((value, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: index + 1,
          c: colIndex,
        });

       if (colIndex === 7) {
  ws[cellAddress] = {
    v: value,
    t: "d",
    z: "dd-mmm-yyyy",
    s: rowStyle,
  };
} else {
  ws[cellAddress] = {
    v: value,
    t:
      colIndex === 1
        ? empIsNum ? "n" : "s"
        : typeof value === "number"
        ? "n"
        : "s",
    s: rowStyle,
  };
}
      });
    });

    // =========================
    // RANGE
    // =========================
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: tableData.length, c: headers.length - 1 },
    });

    // =========================
    // COLUMN WIDTH
    // =========================
    ws["!cols"] = headers.map(() => ({ wch: 20 }));

    // =========================
    // FREEZE HEADER
    // =========================
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    // =========================
    // FILTER
    // =========================
    ws["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1`,
    };

    // =========================
    // ADD SHEET
    // =========================
    XLSX.utils.book_append_sheet(wb, ws, "POB Report");

    // =========================
    // DOWNLOAD
    // =========================
    XLSX.writeFile(wb, `POB_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  // make clickable cell props for accessibility and uniform styling
  const makeCellProps = (value, onClickFn) => {
    if (!value || Number(value) === 0) {
      return {
        className: "px-2 py-2 border text-gray-500 cursor-default",
      };
    }

    return {
      role: "button",
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClickFn();
        }
      },
      onClick: onClickFn,
      className:
        "px-2 py-2 border cursor-pointer text-blue-600 underline hover:text-blue-800 focus:outline-none",
    };
  };
  const handleNavigation = (path, employeeId) => {
    const filterState = {
      selectedState,
      selectedDivision,
      selectedEmployee,
      pobMonthYear,
      tableData,
      divisions,
      employees,
    };

    const [month, year] = pobMonthYear.split("-");
    const url = `${path}?month=${month}&year=${year}&employee_id=${employeeId}`;
    localStorage.setItem("pobDataState", JSON.stringify(filterState));
    const windowFeatures =
      "width=1200,height=700,menubar=no,toolbar=no,status=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", windowFeatures);
  };

  const handleGoButtonClick = () => {
    setShowContainers(true);
    handleGoClick();
  };

  const handleRetry = () => {
    setError(null);
    handleGoClick();
  };

  return (
    <div className="p-4 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}

      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">POB</span>{" "}
          <span className="text-blue-500">Report</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <label className="text-right text-xs">State</label>
          <select
            className={`w-full mt-1 border rounded px-2 py-1 text-xs ${error ? "ring-2 ring-red-400" : ""
              }`}
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">All</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right text-xs">Division</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center mt-3">
          <label className="text-right text-xs">Employee</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={isLoading}
          >
            <option key="All" value="All">
              All
            </option>
            {employees
              .filter((emp) => String(emp.id) !== "All")
              .map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name} ${emp.employee} | ${emp.designation || ""
                    } | ${emp.headquarter_name || ""} | ${emp.division || ""} | ${emp.state_name || ""
                    }`}
                </option>
              ))}
          </select>

          <label className="text-right text-xs">Month / Year</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={pobMonthYear}
            onChange={(e) => setPobMonthYear(e.target.value)}
            disabled={isLoading}
          >
            {monthYearOptions.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoButtonClick}
            disabled={isLoading}
            className="break-normal"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {showContainers && (
        <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50 mt-5">
          <div>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-lg text-gray-600">Loading data...</div>
              </div>
            ) : (
              <div
                ref={tableWrapperRef}
                className="border rounded shadow  table-wrapper"
              >
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="px-2 py-2 border">S.No</th>
                      <th className="px-2 py-2 border">EMP ID</th>
                      <th className="px-2 py-2 border text-left">Employee</th>
                      <th className="px-2 py-2 border">State</th>
                      <th className="px-2 py-2 border">HQ</th>
                      <th className="px-2 py-2 border">Division</th>
                      <th className="px-2 py-2 border">Designation</th>
                      <th className="px-2 py-2 border">DOJ</th>
                      <th className="px-2 py-2 border">Manager</th>
                      <th className="px-2 py-2 border">No Of Doctors</th>
                      <th className="px-2 py-2 border">No Of Retailers</th>
                      <th className="px-2 py-2 border">No Of Stockist</th>
                      <th className="px-2 py-2 border">No Of Route</th>
                      <th className="px-2 py-2 border">POB Total</th>
                      <th className="px-2 py-2 border">POB DateWise</th>
                      <th className="px-2 py-2 border">Stockist Wise</th>
                      <th className="px-2 py-2 border">
                        Cumulative <br />
                        Stockist Wise
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.length > 0 ? (
                      tableData.map((row, index) => (
                        <tr
                          key={`${row.employee_id}-${index}`}
                          className={index % 2 === 0 ? "" : "bg-gray-50"}
                        >
                          <td className="px-2 py-2 border">{index + 1}</td>
                          <td className="px-2 py-2 border">{row.user_id}</td>
                          <td className="px-2 py-2 border text-left">
                            {row.employee_name}
                          </td>
                          <td className="px-2 py-2 border">{row.state}</td>
                          <td className="px-2 py-2 border">{row.hq}</td>
                          <td className="px-2 py-2 border">{row.division}</td>
                          <td className="px-2 py-2 border">
                            {row.designation}
                          </td>
                          <td className="px-2 py-2 border">
                            {formatDate(row.doj)}
                          </td>
                          <td className="px-2 py-2 border">{row.manager}</td>

                          <td
                            {...makeCellProps(row.doctor_count, () =>
                              handleNavigation(
                                `/doctordetails/${row.employee_id}/doctor`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.doctor_count}
                          </td>

                          <td
                            {...makeCellProps(row.retailer_count, () =>
                              handleNavigation(
                                `/chemistdetails/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.retailer_count}
                          </td>

                          <td
                            {...makeCellProps(row.stockist_count, () =>
                              handleNavigation(
                                `/stockistdetails/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.stockist_count}
                          </td>

                          <td className="px-2 py-2 border">
                            {row.route_count}
                          </td>

                          <td
                            {...makeCellProps(row.monthly_pob, () =>
                              handleNavigation(
                                `/pob-details-doctor-chemist/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.monthly_pob}
                          </td>

                          <td
                            {...makeCellProps(row.monthly_pob, () =>
                              handleNavigation(
                                `/pob-details-doctor-chemist-datewise/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.monthly_pob}
                          </td>

                          <td
                            {...makeCellProps(row.monthly_pob, () =>
                              handleNavigation(
                                `/pob-details-doctor-chemist-stockistwise/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.monthly_pob}
                          </td>

                          <td
                            {...makeCellProps(row.monthly_pob, () =>
                              handleNavigation(
                                `/pob-details-stockist/${row.employee_id}`,
                                row.employee_id,
                              ),
                            )}
                          >
                            {row.monthly_pob}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={17}
                          className="px-4 py-6 text-center text-gray-500"
                        >
                          No data found for the selected filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={handleExportExcel}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PobData;
